"use client";

import type React from "react";

import { useEffect, useState } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  Tabs,
  Tab,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Divider,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  FormControlLabel,
  Checkbox,
  DialogActions,
} from "@mui/material";
import { Business, School, Edit, Add, DeleteForever } from "@mui/icons-material";
import { apiRemote, ICountry, useApiCache } from "@/api";
import { useUser } from "@/contexts/UserContext";
import SelectWithSearch from "@/components/SelectWithSearch";
import { useAlert } from "@/contexts/AlertContext";

const calculateProfileCompletion = (profile: any) => {
  // Định nghĩa tất cả các trường cần kiểm tra dưới dạng một mảng các giá trị boolean
  const checks = [
      !!profile?.educator_info?.headline,
      !!profile?.educator_info?.country_id,
      !!profile?.educator_professional?.current_role,
      !!profile?.educator_professional?.primary_organization,
      !!profile?.educator_professional?.years_of_experience,
      !!profile?.educator_professional?.about_me_summary,
      (profile?.educator_work_experience?.length > 0),
      (profile?.educator_education?.length > 0),
      (profile?.educator_expertise?.length > 0),
      !!profile?.educator_professional_links?.[0]?.link_url,
  ];

  // Lấy tổng số trường cần kiểm tra một cách tự động
  const totalFields = checks.length;
  
  // Đếm số trường đã hoàn thành (giá trị true trong mảng)
  const completedFields = checks.filter(Boolean).length;
  
  // Trả về phần trăm (làm tròn)
  return Math.round((completedFields / totalFields) * 100);
};

export default function ProfileSetupPage() {
  const [activeTab, setActiveTab] = useState(0);
  const [profile, setProfile] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const { user, setUser } = useUser();
  const [countries, setCountries] = useState<ICountry[]>([]);
  const apiCache = useApiCache();
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSending, setIsSending] = useState(false)

  // State mới để quản lý modal và form
  const [isExperienceModalOpen, setIsExperienceModalOpen] = useState(false);
  const [isEducationModalOpen, setIsEducationModalOpen] = useState(false);

  const [editingExperience, setEditingExperience] = useState<any>(null);
  const [editingEducation, setEditingEducation] = useState<any>(null);
  const alert = useAlert();

  const [linkedinUrlError, setLinkedinUrlError] = useState('');
  const [profileCompletion, setProfileCompletion] = useState(0);

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailError1, setEmailError1] = useState('');

  // State cho form Add Experience
  const [newExperience, setNewExperience] = useState({
    title: "",
    organization: "",
    start_date: "",
    end_date: "",
    is_current: false,
  });

  // State cho form Add Education
  const [newEducation, setNewEducation] = useState({
    degree: "",
    field_of_study: "",
    university: "",
    start_year: "",
    end_year: "",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await apiRemote.getEducatorInfo(user?.user_login);
        setProfile({ 
          ...res.data,
          first_name: user?.first_name || "",
          last_name: user?.last_name || "",
          email: user?.user_email || "",
          old_email: user?.user_email || ""
      });
      } catch (error) {
        console.error("Error fetching profile:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    apiCache
      .getCountryList()
      .then((data) => {
        setCountries(data);
      })
      .catch((err) => {
        console.log(err);
      });
  }, []);

  useEffect(() => {
    // Tính toán và cập nhật phần trăm hoàn thành mỗi khi profile thay đổi
    const percentage = calculateProfileCompletion(profile);
    setProfileCompletion(percentage);
  }, [profile]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleSave = async () => {
    setEmailError1('');

    // Biểu thức chính quy cơ bản để kiểm tra định dạng email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(profile.email)) {
      setEmailError1('Invalid email');
      return; // Ngừng thực thi nếu email không đúng định dạng
    }
    setIsSubmitting(true);
    try {
      apiRemote.updateProfesionalInfo(profile).then((data)=>{
        if(data.code == 200){
          alert.success("Updated Successfully!")
          const newUser = {
            ...user,
            first_name: profile.first_name,
            last_name: profile.last_name,
            display_name: profile.first_name + ' ' + profile.last_name,
            user_email: profile.email
          };
          setUser(newUser);
        }else{
          alert.error(data.message)
        }
        setIsSubmitting(false);
      }).catch((e)=>{
        console.log(e)
        setIsSubmitting(false);
      })
    } catch (error) {
      console.error("Error updating profile:", error);
      // Bạn có thể thêm một thông báo lỗi ở đây
    } finally {
      
    }
  };

  const handleSend = async () => {
    // Reset lỗi trước khi validate mới
    setEmailError('');

    // Biểu thức chính quy cơ bản để kiểm tra định dạng email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email) {
      setEmailError('Email cannot be empty.');
      return; // Ngừng thực thi nếu email trống
    }

    if (!emailRegex.test(email)) {
      setEmailError('Invalid email');
      return; // Ngừng thực thi nếu email không đúng định dạng
    }
    setIsSending(true);
    try {
      apiRemote.sendVerifyEducatorEmail({
        email: email
      }).then((data)=>{
        alert.success("Success")
        setIsSending(false);
      }).catch((e)=>{
        console.log(e)
        setIsSending(false);
      })
    } catch (error) {
      console.error("Error updating profile:", error);
      // Bạn có thể thêm một thông báo lỗi ở đây
    } finally {
      
    }
  };

  const handleCloseExperienceModal = () => {
    setIsExperienceModalOpen(false);
    setEditingExperience(null); // Rất quan trọng: reset state
  };

  const handleOpenAddExperience = () => {
    setEditingExperience({
      title: "",
      organization: "",
      start_date: "",
      end_date: "",
      is_current: false,
    });
    setIsExperienceModalOpen(true);
  };

  const handleOpenEditExperience = (experience: any, index: number) => {
    setEditingExperience({ ...experience, originalIndex: index });
    setIsExperienceModalOpen(true);
  };

  // Thêm kinh nghiệm mới vào profile
  const handleSaveExperience = () => {
    let updatedExperiences = [...profile.educator_work_experience];

    if (editingExperience.originalIndex !== undefined) {
      // Chế độ EDIT: tìm và cập nhật item cũ
      updatedExperiences[editingExperience.originalIndex] = editingExperience;
    } else {
      // Chế độ ADD: thêm item mới vào cuối mảng
      updatedExperiences = [...updatedExperiences, editingExperience];
    }

    setProfile((prev: any) => ({
      ...prev,
      educator_work_experience: updatedExperiences,
    }));

    handleCloseExperienceModal();
  };

  const handleCloseEducationModal = () => {
    setIsEducationModalOpen(false);
    setEditingEducation(null);
  };

  // Mở modal thêm học vấn
  const handleOpenAddEducation = () => {
    setEditingEducation({
      degree: "",
      field_of_study: "",
      university: "",
      start_year: "",
      end_year: "",
    });
    setIsEducationModalOpen(true);
  };

  const handleOpenEditEducation = (education: any, index: number) => {
    setEditingEducation({ ...education, originalIndex: index });
    setIsEducationModalOpen(true);
  };

  const handleSaveEducation = () => {
    let updatedEducation = [...profile.educator_education];

    if (editingEducation.originalIndex !== undefined) {
      // Chế độ EDIT
      updatedEducation[editingEducation.originalIndex] = editingEducation;
    } else {
      // Chế độ ADD
      updatedEducation = [...updatedEducation, editingEducation];
    }

    setProfile((prev: any) => ({
      ...prev,
      educator_education: updatedEducation,
    }));
    handleCloseEducationModal();
  };

  const handleDeleteExperience = (index: number) => {
    const updatedExperiences = profile.educator_work_experience.filter(
      (_: any, i: number) => i !== index
    );
    setProfile((prev: any) => ({
      ...prev,
      educator_work_experience: updatedExperiences,
    }));
  };

  const handleDeleteEducation = (index: number) => {
    const updatedEducation = profile.educator_education.filter(
      (_: any, i: number) => i !== index
    );
    setProfile((prev: any) => ({
      ...prev,
      educator_education: updatedEducation,
    }));
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" fontWeight={700} color="#2c3e50" gutterBottom>
          Edit Your Professional Profile
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Keep your information up to date to help others connect with you.
        </Typography>
      </Box>

      {/* Progress Card */}
      <Card
        sx={{
          mb: 4,
          borderRadius: 3,
          boxShadow: "0 8px 25px rgba(44, 62, 80, 0.09)",
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
            <Typography variant="h6" fontWeight={600}>
                Profile Completion
            </Typography>
            <Box sx={{ flexGrow: 1 }}>
                <LinearProgress
                    variant="determinate"
                    value={profileCompletion} // Sử dụng state mới
                    sx={{ height: 12, borderRadius: 6 }}
                />
            </Box>
            <Typography variant="h6" fontWeight={700} color="primary">
                {profileCompletion}%
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Main Editor */}
      <Paper
        sx={{
          borderRadius: 4,
          boxShadow: "0 8px 25px rgba(44, 62, 80, 0.09)",
          overflow: "hidden",
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            bgcolor: "#f1f3f6",
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Tab label="Profile Info" sx={{ fontWeight: 600, py: 2.5, px: 4 }} />
          <Tab
            label="Experience & Education"
            sx={{ fontWeight: 600, py: 2.5, px: 4 }}
          />
          <Tab
            label="Verification & Settings"
            sx={{ fontWeight: 600, py: 2.5, px: 4 }}
          />
        </Tabs>

        <Box sx={{ p: 5 }}>
          {/* Tab 1: Profile Info */}
          {activeTab === 0 && (
            <Box>
              <Typography
                variant="h4"
                fontWeight={700}
                color="#2c3e50"
                sx={{ mb: 4, pb: 2, borderBottom: 1, borderColor: "divider" }}
              >
                Core Identity & Credentials
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    value={profile.first_name || ""}
                    variant="outlined"
                    onChange={(e) =>
                      setProfile((prev: any) => ({
                        ...prev,
                        first_name: e.target.value
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    value={profile.last_name || ""}
                    variant="outlined"
                    onChange={(e) =>
                      setProfile((prev: any) => ({
                        ...prev,
                        last_name: e.target.value
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={profile.email || ""}
                    variant="outlined"
                    type="email"
                    onChange={(e) =>
                      setProfile((prev: any) => ({
                        ...prev,
                        email: e.target.value
                      }))
                    }
                    error={!!emailError1} // Bật trạng thái lỗi nếu có thông báo lỗi
                    helperText={emailError1 || ""} // Hiển thị thông báo lỗi
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Headline"
                    defaultValue={profile?.educator_info?.headline || ""}
                    onChange={(e) =>
                      setProfile((prev: any) => ({
                        ...prev,
                        educator_info: {
                          ...prev.educator_info,
                          headline: e.target.value,
                        },
                      }))
                    }
                    variant="outlined"
                    helperText="A short, professional summary that appears under your name."
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <SelectWithSearch
                      options={countries.map((item) => ({
                        value: item.id.toString(),
                        label: item.country_name,
                      }))}
                      label={"Country"}
                      value={profile?.educator_info?.country_id}
                      required={false}
                      disabled={false}
                      onChange={(value) => {
                        setProfile((prev: any) => ({
                          ...prev,
                          educator_info: {
                            ...prev.educator_info,
                            country_id: value.value.toString(),
                          },
                        }));
                      }}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Current Role / Title"
                    defaultValue={
                      profile?.educator_professional?.current_role || ""
                    }
                    onChange={(e) =>
                      setProfile((prev: any) => ({
                        ...prev,
                        educator_professional: {
                          ...prev.educator_professional,
                          current_role: e.target.value,
                        },
                      }))
                    }
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Primary School / Organization"
                    defaultValue={
                      profile?.educator_professional?.primary_organization || ""
                    }
                    onChange={(e) =>
                      setProfile((prev: any) => ({
                        ...prev,
                        educator_professional: {
                          ...prev.educator_professional,
                          primary_organization: e.target.value,
                        },
                      }))
                    }
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Years of Experience</InputLabel>
                    <Select
                      label="Years of Experience"
                      value={
                        profile?.educator_professional?.years_of_experience ||
                        ""
                      }
                      onChange={(e) =>
                        setProfile((prev: any) => ({
                          ...prev,
                          educator_professional: {
                            ...prev.educator_professional,
                            years_of_experience: e.target.value,
                          },
                        }))
                      }
                    >
                      <MenuItem value="10+ years">10+ years</MenuItem>
                      <MenuItem value="5-10 years">5-10 years</MenuItem>
                      <MenuItem value="5+ years">5+ years</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Subjects / Areas of Expertise"
                    defaultValue={
                      profile?.educator_expertise
                        ?.map((exp: any) => exp.area_of_expertise)
                        .join(", ") || ""
                    }
                    onChange={(e) => {
                      const expertiseArray = e.target.value
                        .split(",")
                        .map((item) => ({ area_of_expertise: item.trim() }));
                      setProfile((prev: any) => ({
                        ...prev,
                        educator_expertise: expertiseArray,
                      }));
                    }}
                    variant="outlined"
                    helperText="Add tags that best describe your skills."
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="About Me / Professional Summary"
                    defaultValue={
                      profile?.educator_professional?.about_me_summary || ""
                    }
                    onChange={(e) =>
                      setProfile((prev: any) => ({
                        ...prev,
                        educator_professional: {
                          ...prev.educator_professional,
                          about_me_summary: e.target.value,
                        },
                      }))
                    }
                    variant="outlined"
                    multiline
                    rows={5}
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Tab 2: Experience & Education */}
          {activeTab === 1 && (
            <Box>
              {/* Work Experience */}
              <Box sx={{ mb: 5 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                  }}
                >
                  <Typography variant="h4" fontWeight={700} color="#2c3e50">
                    Work Experience
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={handleOpenAddExperience}
                    sx={{ borderRadius: 2 }}
                  >
                    Add Experience
                  </Button>
                </Box>

                {profile?.educator_work_experience?.map(
                  (exp: any, index: number) => (
                    <Card key={index} sx={{ mb: 2, borderRadius: 3 }}>
                      <CardContent sx={{ p: 3 }}>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 2.5,
                            alignItems: "flex-start",
                          }}
                        >
                          <Business
                            sx={{
                              fontSize: 32,
                              color: "primary.main",
                              mt: 0.5,
                            }}
                          />
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="h6" fontWeight={600}>
                              {exp.title}
                            </Typography>
                            <Typography
                              variant="body1"
                              fontWeight={500}
                              sx={{ mb: 0.5 }}
                            >
                              {exp.organization}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {new Date(exp.start_date).toLocaleDateString(
                                "en-US",
                                { month: "short", year: "numeric" }
                              )}{" "}
                              -{" "}
                              {exp.is_current
                                ? "Present"
                                : new Date(exp.end_date).toLocaleDateString(
                                    "en-US",
                                    { month: "short", year: "numeric" }
                                  )}
                            </Typography>
                          </Box>
                          <IconButton onClick={() => handleOpenEditExperience(exp, index)}>
                              <Edit />
                          </IconButton>
                          <IconButton onClick={() => handleDeleteExperience(index)}>
                              <DeleteForever />
                          </IconButton>
                        </Box>
                      </CardContent>
                    </Card>
                  )
                )}
              </Box>

              {/* Education */}
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                  }}
                >
                  <Typography variant="h4" fontWeight={700} color="#2c3e50">
                    Education
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={handleOpenAddEducation}
                    sx={{ borderRadius: 2 }}
                  >
                    Add Education
                  </Button>
                </Box>
                {profile?.educator_education?.map((edu: any, index: number) => (
                  <Card key={index} sx={{ mb: 2, borderRadius: 3 }}>
                    <CardContent sx={{ p: 3 }}>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 2.5,
                          alignItems: "flex-start",
                        }}
                      >
                        <School
                          sx={{ fontSize: 32, color: "primary.main", mt: 0.5 }}
                        />
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="h6" fontWeight={600}>
                            {edu.degree} ({edu.field_of_study})
                          </Typography>
                          <Typography
                            variant="body1"
                            fontWeight={500}
                            sx={{ mb: 0.5 }}
                          >
                            {edu.university}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {edu.start_year} - {edu.end_year}
                          </Typography>
                        </Box>
                        <IconButton onClick={() => handleOpenEditEducation(edu, index)}>
                            <Edit />
                        </IconButton>
                        <IconButton onClick={() => handleDeleteEducation(index)}>
                            <DeleteForever />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Box>
          )}

          {editingExperience !== null && (
              <Dialog open={isExperienceModalOpen} onClose={handleCloseExperienceModal} fullWidth maxWidth="sm">
                  <DialogTitle fontWeight={600}>
                      {editingExperience?.originalIndex !== undefined ? "Edit Experience" : "Add New Experience"}
                  </DialogTitle>
                  <DialogContent dividers>
                      <Grid container spacing={2}>
                          <Grid item xs={12}>
                              <TextField fullWidth label="Title" value={editingExperience?.title} onChange={(e) => setEditingExperience({ ...editingExperience, title: e.target.value })} />
                          </Grid>
                          <Grid item xs={12}>
                              <TextField fullWidth label="Organization" value={editingExperience?.organization} onChange={(e) => setEditingExperience({ ...editingExperience, organization: e.target.value })} />
                          </Grid>
                          <Grid item xs={6}>
                              <TextField
                                  fullWidth
                                  label="Start Date"
                                  type="date"
                                  InputLabelProps={{ shrink: true }}
                                  value={editingExperience?.start_date}
                                  onChange={(e) => setEditingExperience({ ...editingExperience, start_date: e.target.value })}
                              />
                          </Grid>
                          <Grid item xs={6}>
                              <TextField
                                  fullWidth
                                  label="End Date"
                                  type="date"
                                  InputLabelProps={{ shrink: true }}
                                  value={editingExperience?.end_date}
                                  disabled={editingExperience?.is_current}
                                  onChange={(e) => setEditingExperience({ ...editingExperience, end_date: e.target.value })}
                              />
                          </Grid>
                          <Grid item xs={12}>
                              <FormControlLabel
                                  control={<Checkbox checked={editingExperience?.is_current} onChange={(e) => setEditingExperience({ ...editingExperience, is_current: e.target.checked })} />}
                                  label="I currently work here"
                              />
                          </Grid>
                      </Grid>
                  </DialogContent>
                  <DialogActions>
                      <Button onClick={handleCloseExperienceModal}>Cancel</Button>
                      <Button onClick={handleSaveExperience} variant="contained">
                          {editingExperience?.originalIndex !== undefined ? "Save Changes" : "Add Experience"}
                      </Button>
                  </DialogActions>
              </Dialog>
          )}

          {/* Modal thêm/chỉnh sửa học vấn */}
          {editingEducation !== null && (
              <Dialog open={isEducationModalOpen} onClose={handleCloseEducationModal} fullWidth maxWidth="sm">
                  <DialogTitle fontWeight={600}>
                      {editingEducation?.originalIndex !== undefined ? "Edit Education" : "Add New Education"}
                  </DialogTitle>
                  <DialogContent dividers>
                      <Grid container spacing={2}>
                          <Grid item xs={12}>
                              <TextField fullWidth label="Degree" value={editingEducation?.degree} onChange={(e) => setEditingEducation({ ...editingEducation, degree: e.target.value })} />
                          </Grid>
                          <Grid item xs={12}>
                              <TextField fullWidth label="Field of Study" value={editingEducation?.field_of_study} onChange={(e) => setEditingEducation({ ...editingEducation, field_of_study: e.target.value })} />
                          </Grid>
                          <Grid item xs={12}>
                              <TextField fullWidth label="University" value={editingEducation?.university} onChange={(e) => setEditingEducation({ ...editingEducation, university: e.target.value })} />
                          </Grid>
                          <Grid item xs={6}>
                              <TextField
                                  fullWidth
                                  label="Start Year"
                                  type="number"
                                  value={editingEducation?.start_year}
                                  onChange={(e) => {
                                      const value = e.target.value;
                                      // Cho phép chuỗi rỗng để người dùng có thể xóa nội dung
                                      // Hoặc nếu giá trị là số và lớn hơn hoặc bằng 0
                                      if (value === '' || (Number(value) >= 0 && !isNaN(Number(value)))) {
                                          setEditingEducation({ ...editingEducation, start_year: value });
                                      }
                                  }}
                                  inputProps={{ min: "0" }} // Thêm thuộc tính min để gợi ý không cho phép số âm
                              />
                          </Grid>
                          <Grid item xs={6}>
                              <TextField
                                  fullWidth
                                  label="End Year"
                                  type="number"
                                  value={editingEducation?.end_year}
                                  onChange={(e) => {
                                      const value = e.target.value;
                                      // Cho phép chuỗi rỗng để người dùng có thể xóa nội dung
                                      // Hoặc nếu giá trị là số và lớn hơn hoặc bằng 0
                                      if (value === '' || (Number(value) >= 0 && !isNaN(Number(value)))) {
                                          setEditingEducation({ ...editingEducation, end_year: value });
                                      }
                                  }}
                                  inputProps={{ min: "0" }} // Thêm thuộc tính min để gợi ý không cho phép số âm
                              />
                          </Grid>
                      </Grid>
                  </DialogContent>
                  <DialogActions>
                      <Button onClick={handleCloseEducationModal}>Cancel</Button>
                      <Button onClick={handleSaveEducation} variant="contained">
                          {editingEducation?.originalIndex !== undefined ? "Save Changes" : "Add Education"}
                      </Button>
                  </DialogActions>
              </Dialog>
          )}

          {/* Tab 3: Verification & Settings */}
          {activeTab === 2 && (
            <Box>
              <Typography
                variant="h4"
                fontWeight={700}
                color="#2c3e50"
                sx={{ mb: 4, pb: 2, borderBottom: 1, borderColor: "divider" }}
              >
                Trust & Verification
              </Typography>

              {/* <Box sx={{ mb: 4 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Official Email Verification
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Add your official work email to earn a "Verified Educator"
                  badge. We'll send a confirmation link to this address. It will
                  not be displayed publicly.
                </Typography>
                <Box sx={{ display: "flex", gap: 1.5 }}>
                  <TextField
                    fullWidth
                    placeholder="e.g., j.doe@maplewood.edu"
                    variant="outlined"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    error={!!emailError} // Bật trạng thái lỗi nếu có thông báo lỗi
                    helperText={emailError || ""} // Hiển thị thông báo lỗi
                  />
                  <Button
                    variant="contained"
                    sx={{ whiteSpace: "nowrap", bgcolor: "#4A90E2" }}
                    disabled={isSending}
                    onClick={handleSend}
                  >
                    
                    {isSending ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Send Verification Link"
                    )}
                  </Button>
                </Box>
              </Box> */}

              <Box>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  LinkedIn Profile URL
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Linking your LinkedIn profile is a strong signal of
                  professional identity.
                </Typography>
                <TextField
                  fullWidth
                  placeholder="https://www.linkedin.com/in/your-profile"
                  variant="outlined"
                  defaultValue={
                    profile?.educator_professional_links?.[0]?.link_url || ""
                  }
                  error={!!linkedinUrlError} // Bật trạng thái lỗi nếu có thông báo lỗi
                  helperText={linkedinUrlError || "Linking your LinkedIn profile is a strong signal of professional identity."} // Hiển thị thông báo lỗi
                  onChange={(e) => {
                    const newLinkUrl = e.target.value;
                    const linkedinRegex = /^(http(s)?:\/\/)?(www\.)?linkedin\.com\/(in|pub|company)\/[a-zA-Z0-9_-]+(\/)?$/;

                    if (newLinkUrl && !linkedinRegex.test(newLinkUrl)) {
                      setLinkedinUrlError('Invalid URL. Please enter a valid LinkedIn URL format.');
                    }else{
                      setLinkedinUrlError('');
                      setProfile((prev: any) => {
                        // Lấy bản sao của mảng hiện tại, hoặc tạo mảng rỗng nếu chưa có
                        const updatedLinks = prev.educator_professional_links ? [...prev.educator_professional_links] : [];
                        
                        // Nếu đã có một item ở index 0, cập nhật nó
                        if (updatedLinks.length > 0) {
                            updatedLinks[0] = {
                                ...updatedLinks[0],
                                link_url: newLinkUrl,
                            };
                        } else {
                            // Nếu chưa có, thêm một item mới vào mảng
                            updatedLinks.push({
                                link_url: newLinkUrl,
                                link_type: "linkedin",
                            });
                        }
            
                        return {
                            ...prev,
                            educator_professional_links: updatedLinks,
                        };
                      });
                    }
                    
                  }}
                />
              </Box>
            </Box>
          )}

          <Divider sx={{ my: 4 }} />

          <Box sx={{ textAlign: "right" }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleSave} // Thêm onClick ở đây
              sx={{
                bgcolor: "#4A90E2",
                "&:hover": { bgcolor: "#357ABD" },
                px: 4,
                py: 1.5,
              }}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Save All Changes"
              )}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
